import type { ComponentProps, ReactNode } from 'react';

import type { Size } from './types';
import { cn } from '../cn';
import { General, Shapes } from '../StrokeIcons';
import { sizeFont, sizeHeight, sizeIconClass, sizeInnerGap, sizePaddingL, sizePaddingR, sizeRounded } from './types';

export type CheckboxOption = {
  value: string;
  label: string;
  /** Optional leading visual between the box and the label (a color dot, an avatar). */
  icon?: ReactNode;
  /** Optional trailing content, right-aligned (e.g. a count or an amount). */
  meta?: ReactNode;
  /** Disable just this option (the group-level `disabled` disables all of them). */
  disabled?: boolean;
};

export type CheckboxProps = Omit<ComponentProps<'div'>, 'size' | 'onChange'> & {
  size?: Size;
  bordered?: boolean;
  text?: boolean;
  error?: string;
  disabled?: boolean;
  name?: string;
  options: CheckboxOption[];
  value?: string[];
  onChange?: (value: string[]) => void;
};

export default function Checkbox({
  size = 'md',
  bordered = true,
  text,
  error,
  disabled,
  name,
  options,
  value = [],
  onChange,
  className,
  ...props
}: CheckboxProps) {
  function toggle(optionValue: string, optionDisabled: boolean) {
    if (optionDisabled) return;
    const next = value.includes(optionValue) ? value.filter((v) => v !== optionValue) : [...value, optionValue];
    onChange?.(next);
  }

  return (
    <div
      {...props}
      className={cn(
        'inline-flex flex-col overflow-hidden font-sans transition-colors divide-y',
        sizeRounded(size),
        sizeFont(size),
        'border',
        bordered &&
          !text &&
          !error &&
          'border-slate-200 dark:border-charade-700 divide-slate-200 dark:divide-charade-700',
        bordered &&
          !text &&
          error &&
          'border-fair-pink-300 dark:border-fair-pink-900 divide-fair-pink-300 dark:divide-fair-pink-900',
        (!bordered || text) && 'border-transparent',
        !text && !disabled && !error && 'bg-white dark:bg-charade-950',
        !text && !disabled && error && 'bg-fair-pink-100 dark:bg-fair-pink-950',
        !text && disabled && 'bg-stone-50 dark:bg-slate-950',
        text && 'bg-transparent',
        !disabled && !error && 'text-slate-600 dark:text-slate-100',
        disabled && 'text-stone-400 dark:text-slate-700',
        error && 'text-fair-pink-600 dark:text-fair-pink-400',
        className,
      )}>
      {options.map((opt) => {
        const checked = value.includes(opt.value);
        const optDisabled = disabled === true || opt.disabled === true;
        return (
          <label
            key={opt.value}
            className={cn(
              'inline-flex items-center cursor-pointer transition-colors',
              sizeHeight(size),
              sizeInnerGap(size),
              !text && !optDisabled && !checked && 'hover:bg-haiti-100 dark:hover:bg-haiti-900',
              !text && checked && 'bg-haiti-50 dark:bg-haiti-950',
              text && !optDisabled && 'hover:bg-slate-100 dark:hover:bg-slate-800',
              optDisabled && 'cursor-not-allowed',
              // A lone disabled option greys out; a disabled group already does via its text color.
              !disabled && opt.disabled === true && 'text-stone-400 dark:text-slate-600',
            )}>
            <input
              type="checkbox"
              name={name}
              value={opt.value}
              checked={checked}
              onChange={() => toggle(opt.value, optDisabled)}
              disabled={optDisabled}
              className="hidden"
            />
            <span className={cn('shrink-0 flex items-center justify-center', sizePaddingL(size))}>
              <span className={cn(sizeIconClass(size), '*:size-full', checked && 'text-haiti-500')}>
                {checked ? <General.CheckSquare /> : <Shapes.Square />}
              </span>
            </span>
            {opt.icon != null && (
              <span className={cn(sizeIconClass(size), 'shrink-0 flex items-center justify-center')}>{opt.icon}</span>
            )}
            {/* title: long labels (emails, feature keys) truncate — hover shows them whole. */}
            <span className={cn('flex-1 min-w-0 truncate', opt.meta == null && sizePaddingR(size))} title={opt.label}>
              {opt.label}
            </span>
            {opt.meta != null && <span className={cn('shrink-0', sizePaddingR(size))}>{opt.meta}</span>}
          </label>
        );
      })}
    </div>
  );
}
